import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addTeamMember } from "api/landlord/landlord";
import { FC } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Input,
  Modal
} from "shared/components";
import { getErrorMessage } from "utils/error";
import { useForm } from "shared/hooks";
import { object, string } from "yup";
import { toast } from "react-toastify";
import { ROLE } from "providers/Auth";

type Props = {
  opened: boolean;
  onClose: () => void;
};
const invite = object({
  name: string().required("Name is required"),
  email: string().email().required("Email is required")
});

export const InviteNewMemberModal: FC<Props> = ({ opened, onClose }) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values, reset } = useForm(
    {
      name: "",
      email: ""
    },
    invite
  );

  const { mutate } = useMutation({
    mutationFn: (data: { name: string; email: string; role: ROLE }) => {
      return addTeamMember(data);
    },
    onSuccess: () => {
      toast.success(`${values.name} was invited`);
      queryClient.invalidateQueries({ queryKey: ["GET_TEAM_MEMBERS"] });
      reset();
      onClose();
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      panelClassName="w-[366px] p-6"
    >
      <header className="mb-4 text-center text-xl font-bold text-content-1000">
        Invite a new member
      </header>
      <form
        onSubmit={onSubmit({
          onSuccess: () =>
            mutate({ name: values.name, email: values.email, role: ROLE.Admin })
        })}
      >
        <div className="mb-4">
          <Input
            id="full_name"
            label="Full Name"
            placeholder="Jakob Dias"
            required={true}
            {...register("name")}
          />
        </div>
        <Input
          id="email"
          label="Email"
          placeholder="example@gmail.com"
          required={true}
          {...register("email")}
        />
        <div className="mt-10 flex gap-2">
          <div className="w-full">
            <Button size={ButtonSize.BASE} onClick={onClose} className="w-full">
              Cancel
            </Button>
          </div>
          <div className="w-full">
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              type="submit"
              className="w-full"
            >
              Invite
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
